import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Clock, Calendar as CalendarIcon, Plus, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface DaySchedule {
  day: number;
  name: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
  slotDuration: number;
  allowAfterHours: boolean;
  afterHoursStart: string;
  afterHoursEnd: string;
}

interface BlockedSlot {
  id: string;
  blocked_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  is_all_day: boolean;
}

export default function Availability() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Weekly schedule state
  const [schedule, setSchedule] = useState<DaySchedule[]>([
    { day: 0, name: 'Sunday', enabled: false, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 1, name: 'Monday', enabled: true, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 2, name: 'Tuesday', enabled: true, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 3, name: 'Wednesday', enabled: true, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 4, name: 'Thursday', enabled: true, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 5, name: 'Friday', enabled: true, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '17:00', afterHoursEnd: '20:00' },
    { day: 6, name: 'Saturday', enabled: false, startTime: '09:00', endTime: '17:00', slotDuration: 60, allowAfterHours: false, afterHoursStart: '09:00', afterHoursEnd: '15:00' },
  ]);

  // Blocked slots state
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockDate, setBlockDate] = useState<Date | undefined>();
  const [blockAllDay, setBlockAllDay] = useState(true);
  const [blockStartTime, setBlockStartTime] = useState('09:00');
  const [blockEndTime, setBlockEndTime] = useState('17:00');
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    if (user) {
      fetchAvailability();
      fetchBlockedSlots();
    }
  }, [user]);

  const fetchAvailability = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_availability')
        .select('*')
        .eq('admin_id', user?.id);

      if (error) throw error;

      if (data && data.length > 0) {
        const updatedSchedule = schedule.map((day) => {
          const availData = data.find((a) => a.day_of_week === day.day);
          if (availData) {
            return {
              ...day,
              enabled: availData.is_available,
              startTime: availData.start_time,
              endTime: availData.end_time,
              slotDuration: availData.slot_duration_minutes || 60,
              allowAfterHours: availData.allow_after_hours || false,
              afterHoursStart: availData.after_hours_start || '17:00',
              afterHoursEnd: availData.after_hours_end || '20:00',
            };
          }
          return day;
        });
        setSchedule(updatedSchedule);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      const { data, error } = await supabase
        .from('blocked_time_slots')
        .select('*')
        .eq('admin_id', user?.id)
        .gte('blocked_date', new Date().toISOString().split('T')[0])
        .order('blocked_date', { ascending: true });

      if (error) throw error;
      setBlockedSlots(data || []);
    } catch (error) {
      console.error('Error fetching blocked slots:', error);
    }
  };

  const saveSchedule = async () => {
    if (!user) return;

    try {
      setSaving(true);

      // Delete existing availability
      await supabase.from('admin_availability').delete().eq('admin_id', user.id);

      // Insert new availability
      const availability = schedule
        .filter((day) => day.enabled || day.allowAfterHours)
        .map((day) => ({
          admin_id: user.id,
          day_of_week: day.day,
          start_time: day.startTime,
          end_time: day.endTime,
          slot_duration_minutes: day.slotDuration,
          is_available: day.enabled,
          allow_after_hours: day.allowAfterHours,
          after_hours_start: day.allowAfterHours ? day.afterHoursStart : null,
          after_hours_end: day.allowAfterHours ? day.afterHoursEnd : null,
        }));

      const { error } = await supabase.from('admin_availability').insert(availability);

      if (error) throw error;

      toast({
        title: 'Schedule Saved',
        description: 'Your weekly availability has been updated',
      });
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save schedule',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addBlockedSlot = async () => {
    if (!user || !blockDate) {
      toast({
        title: 'Error',
        description: 'Please select a date',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase.from('blocked_time_slots').insert({
        admin_id: user.id,
        blocked_date: blockDate.toISOString().split('T')[0],
        start_time: blockAllDay ? null : blockStartTime,
        end_time: blockAllDay ? null : blockEndTime,
        reason: blockReason.trim() || null,
        is_all_day: blockAllDay,
      });

      if (error) throw error;

      toast({
        title: 'Date Blocked',
        description: 'The date has been blocked successfully',
      });

      setShowBlockDialog(false);
      setBlockDate(undefined);
      setBlockAllDay(true);
      setBlockStartTime('09:00');
      setBlockEndTime('17:00');
      setBlockReason('');
      fetchBlockedSlots();
    } catch (error: any) {
      console.error('Error blocking slot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to block date',
        variant: 'destructive',
      });
    }
  };

  const deleteBlockedSlot = async (id: string) => {
    try {
      const { error } = await supabase.from('blocked_time_slots').delete().eq('id', id);

      if (error) throw error;

      toast({
        title: 'Block Removed',
        description: 'The date block has been removed',
      });
      fetchBlockedSlots();
    } catch (error: any) {
      console.error('Error deleting blocked slot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove block',
        variant: 'destructive',
      });
    }
  };

  const updateScheduleDay = (dayIndex: number, field: keyof DaySchedule, value: any) => {
    setSchedule((prev) =>
      prev.map((day, idx) => (idx === dayIndex ? { ...day, [field]: value } : day))
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Clock className="h-8 w-8" />
            Availability Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your weekly schedule and blocked dates
          </p>
        </div>

        <Tabs defaultValue="weekly" className="space-y-6">
          <TabsList>
            <TabsTrigger value="weekly">Weekly Schedule</TabsTrigger>
            <TabsTrigger value="blocked">Blocked Dates</TabsTrigger>
          </TabsList>

          <TabsContent value="weekly" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Availability</CardTitle>
                <CardDescription>
                  Set your available days and hours for appointments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                  </div>
                ) : (
                  <>
                    {schedule.map((day, idx) => (
                      <div
                        key={day.day}
                        className="flex flex-col gap-4 p-4 border rounded-lg"
                      >
                        <div className="flex items-center space-x-2 w-32">
                          <Checkbox
                            id={`day-${day.day}`}
                            checked={day.enabled}
                            onCheckedChange={(checked) =>
                              updateScheduleDay(idx, 'enabled', checked)
                            }
                          />
                          <Label htmlFor={`day-${day.day}`} className="font-medium">
                            {day.name}
                          </Label>
                        </div>

                        {day.enabled && (
                          <div className="flex flex-wrap gap-4 flex-1 border-l-2 border-green-500 pl-4">
                            <div className="w-full text-xs font-medium text-green-700 dark:text-green-400 mb-2">
                              Regular Hours (Auto-confirm)
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Start Time</Label>
                              <Input
                                type="time"
                                value={day.startTime}
                                onChange={(e) =>
                                  updateScheduleDay(idx, 'startTime', e.target.value)
                                }
                                className="w-32"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">End Time</Label>
                              <Input
                                type="time"
                                value={day.endTime}
                                onChange={(e) =>
                                  updateScheduleDay(idx, 'endTime', e.target.value)
                                }
                                className="w-32"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Slot Duration
                              </Label>
                              <Select
                                value={day.slotDuration.toString()}
                                onValueChange={(val) =>
                                  updateScheduleDay(idx, 'slotDuration', Number(val))
                                }
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="30">30 min</SelectItem>
                                  <SelectItem value="60">60 min</SelectItem>
                                  <SelectItem value="90">90 min</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`after-hours-${day.day}`}
                              checked={day.allowAfterHours}
                              onCheckedChange={(checked) =>
                                updateScheduleDay(idx, 'allowAfterHours', checked)
                              }
                            />
                            <Label htmlFor={`after-hours-${day.day}`} className="text-sm">
                              Allow after-hours (requires approval)
                            </Label>
                          </div>

                          {day.allowAfterHours && (
                            <div className="flex flex-wrap gap-4 flex-1 border-l-2 border-orange-500 pl-4">
                              <div className="w-full text-xs font-medium text-orange-700 dark:text-orange-400 mb-2">
                                After-Hours (Requires Approval)
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Start Time</Label>
                                <Input
                                  type="time"
                                  value={day.afterHoursStart}
                                  onChange={(e) =>
                                    updateScheduleDay(idx, 'afterHoursStart', e.target.value)
                                  }
                                  className="w-32"
                                />
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">End Time</Label>
                                <Input
                                  type="time"
                                  value={day.afterHoursEnd}
                                  onChange={(e) =>
                                    updateScheduleDay(idx, 'afterHoursEnd', e.target.value)
                                  }
                                  className="w-32"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-end pt-4">
                      <Button onClick={saveSchedule} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Schedule
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blocked" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Blocked Dates</CardTitle>
                    <CardDescription>
                      Block specific dates or times when you're unavailable
                    </CardDescription>
                  </div>
                  <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Block Date
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Block Date</DialogTitle>
                        <DialogDescription>
                          Select a date and time to block from appointments
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Date</Label>
                    <Calendar
                      mode="single"
                      selected={blockDate}
                      onSelect={setBlockDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      className="rounded-md border"
                    />
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="all-day"
                            checked={blockAllDay}
                            onCheckedChange={(checked) => setBlockAllDay(checked as boolean)}
                          />
                          <Label htmlFor="all-day">Block entire day</Label>
                        </div>

                        {!blockAllDay && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Time</Label>
                              <Input
                                type="time"
                                value={blockStartTime}
                                onChange={(e) => setBlockStartTime(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>End Time</Label>
                              <Input
                                type="time"
                                value={blockEndTime}
                                onChange={(e) => setBlockEndTime(e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Reason (Optional)</Label>
                          <Textarea
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                            placeholder="e.g., Vacation, Conference, etc."
                            rows={2}
                          />
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowBlockDialog(false)}
                          >
                            Cancel
                          </Button>
                          <Button onClick={addBlockedSlot}>Block Date</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {blockedSlots.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No blocked dates
                  </div>
                ) : (
                  <div className="space-y-2">
                    {blockedSlots.map((slot) => (
                      <div
                        key={slot.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium">
                            {format(new Date(slot.blocked_date), 'MMMM d, yyyy')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {slot.is_all_day
                              ? 'All day'
                              : `${slot.start_time} - ${slot.end_time}`}
                          </div>
                          {slot.reason && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {slot.reason}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteBlockedSlot(slot.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
